import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ProductList } from '../components/products/ProductList';
import { ProductFilters } from '../components/products/ProductFilters';
import { Store, storeService } from '../services/store';
import { Product, ProductFilters as FilterType, productService } from '../services/product';
import { useBulkSelection } from '../contexts/BulkSelectionContext';
import { Loader2, Grid3X3, List, CheckSquare, Eye, Package2, Layers, RefreshCw, Plus } from 'lucide-react';
import { WarningBanner, BulkSelectionBanner } from '../components/ui/warning-banner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export const Products: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selection, isSelectionActive, selectByCategory, clearSelection } = useBulkSelection();
  const [products, setProducts] = useState<Product[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [bulkSelectLoading, setBulkSelectLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  // Parse filters from URL parameters
  const getFiltersFromURL = (): FilterType => {
    const stockStatus = searchParams.get('stock_status');
    return {
      page: parseInt(searchParams.get('page') || '1'),
      limit: parseInt(searchParams.get('limit') || '20'),
      store_id: searchParams.get('store_id') || undefined,
      search: searchParams.get('search') || undefined,
      category: searchParams.get('category') || undefined,
      has_images: searchParams.get('has_images') === 'true' ? true :
                  searchParams.get('has_images') === 'false' ? false : undefined,
      stock_status: (stockStatus === 'in_stock' || stockStatus === 'out_of_stock') ? stockStatus : undefined,
    };
  };

  // Update URL with current filters
  const updateURL = (newFilters: FilterType) => {
    const params = new URLSearchParams();
    
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value.toString());
      }
    });
    
    setSearchParams(params);
  };

  const [filters, setFilters] = useState<FilterType>(getFiltersFromURL());

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Update filters when URL changes (e.g., browser back/forward)
  useEffect(() => {
    setFilters(getFiltersFromURL());
  }, [searchParams]);

  useEffect(() => {
    loadProducts();
  }, [filters]);

  // Reload categories when store filter changes
  useEffect(() => {
    // Skip on initial load since loadInitialData handles it
    if (!isInitialLoad) {
      loadCategories(filters.store_id);
    }
  }, [filters.store_id]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // PERFORMANCE FIX: Load stores and categories in PARALLEL instead of sequentially
      const [storesData, categoriesData] = await Promise.all([
        storeService.getStores(),
        productService.getCategories(filters.store_id).catch(() => [])
      ]);

      setStores(storesData);

      // Process categories: split comma-separated values and deduplicate
      const processedCategories = categoriesData
        .flatMap((category: string) => category.split(',').map((cat: string) => cat.trim()))
        .filter((category: string) => category !== '')
        .filter((category: string, index: number, array: string[]) => array.indexOf(category) === index)
        .sort();

      setCategories(processedCategories);

      // Mark initial load as complete
      setIsInitialLoad(false);
    } catch (error: any) {
      setError(error.message || 'Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async (storeId?: string) => {
    try {
      console.log('Loading categories for store:', storeId || 'all stores');
      const categoriesData = await productService.getCategories(storeId).catch(() => []);
      
      // Process categories: split comma-separated values and deduplicate
      const processedCategories = categoriesData
        .flatMap((category: string) => category.split(',').map((cat: string) => cat.trim()))
        .filter((category: string) => category !== '')
        .filter((category: string, index: number, array: string[]) => array.indexOf(category) === index)
        .sort();
      
      console.log('Loaded categories:', processedCategories.length, 'categories');
      setCategories(processedCategories);
    } catch (error: any) {
      console.error('Failed to load categories:', error);
      setCategories([]);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await productService.getProducts(filters);
      setProducts(response.products);
      setPagination(response.pagination);
    } catch (error: any) {
      setError(error.message || 'Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const scrollToTop = () => {
    // Try multiple methods to ensure scrolling works
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Also try document.body in case of layout issues
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }
  };

  const handleFiltersChange = (newFilters: FilterType) => {
    // If store changed, reset category filter
    let updatedFilters = { ...newFilters, page: 1 };
    if (newFilters.store_id !== filters.store_id) {
      console.log('Store changed from', filters.store_id, 'to', newFilters.store_id, '- resetting category');
      updatedFilters.category = undefined;
    }
    
    setFilters(updatedFilters);
    updateURL(updatedFilters);
    // Scroll to top when filters change (new search results)
    setTimeout(() => scrollToTop(), 100);
  };

  const handlePageChange = (page: number) => {
    const updatedFilters = { ...filters, page };
    setFilters(updatedFilters);
    updateURL(updatedFilters);
    // Scroll to top when changing pages
    setTimeout(() => scrollToTop(), 100);
  };

  const handleBulkSelectByCategory = async (category: string) => {
    try {
      setBulkSelectLoading(true);
      setError(null);

      const response = await productService.bulkSelectByCategory(category, {
        store_id: filters.store_id,
        include_transformed: false // Only select untransformed products
      });

      selectByCategory(
        category,
        response.selected_ids,
        response.count,
        response.stats,
        response.remaining,
        response.limit_applied,
        response.message
      );

    } catch (error: any) {
      setError(error.message || 'Failed to select products');
    } finally {
      setBulkSelectLoading(false);
    }
  };

  const handleViewBulkReview = () => {
    navigate('/products/bulk-review');
  };

  return (
    <div ref={contentRef} className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between animate-slide-down">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Products</h1>
          <p className="text-muted-foreground">
            Manage and process your product catalog
          </p>
        </div>
        
        {/* Controls */}
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={loadProducts} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          
          {/* Bulk Select Dropdown - Core Feature */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <Select
                onValueChange={handleBulkSelectByCategory}
                disabled={bulkSelectLoading || categories.length === 0}
              >
                <SelectTrigger className="w-64 h-11 bg-primary text-white hover:bg-primary/90 border-primary shadow-lg">
                  <div className="flex items-center">
                    <Layers className="h-5 w-5 mr-2" />
                    <SelectValue placeholder="Bulk Select by Category" />
                  </div>
                </SelectTrigger>
                <SelectContent className="w-64">
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      <div className="flex items-center">
                        <Layers className="h-4 w-4 mr-2 text-primary" />
                        <span className="font-medium">{category}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Highlight badge */}
              <span className="absolute -top-2 -right-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold shadow-md">
                BULK
              </span>
            </div>
            {bulkSelectLoading && (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            )}
          </div>

          {/* View Toggle */}
          <div className="flex items-center bg-muted/50 rounded-lg p-1 border">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="px-3 h-9"
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="px-3 h-9"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Selection Status */}
      {isSelectionActive && (
        <BulkSelectionBanner
          selectedCount={selection.totalCount}
          totalCount={selection.stats?.total || selection.totalCount}
          category={selection.category}
          stats={selection.stats}
          remaining={selection.remaining}
          limitApplied={selection.limitApplied}
          onReview={() => navigate('/products/bulk-review')}
          onClear={clearSelection}
        />
      )}


      {error && (
        <WarningBanner
          variant="error"
          title="Error"
          message={error}
          dismissible
          onDismiss={() => setError('')}
        />
      )}

      <ProductFilters
        stores={stores}
        categories={categories}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <ProductList
        products={products}
        loading={loading}
        pagination={pagination}
        viewMode={viewMode}
        onPageChange={handlePageChange}
      />
    </div>
  );
};